/**
 * Sonuç tablosu (şartname 4 ve 7): gerçek `table`, altı sütun. Satır = otelin seçili oda tipi kombinasyonu.
 * Yatay kayma yalnız bu kapta (`role=region`, odaklanabilir). Durum metni, şekil ve ton `DurumRozeti`nden gelir.
 */
import { Fragment, type ReactNode } from 'react';
import type { HotelPictureView } from '../api';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { DurumRozeti } from './DurumRozeti';
import { SatirFiyatDuzenle } from './SatirFiyatDuzenle';
import { Ikon, Yildizlar } from './Ikon';
import { enAzGece, gunAyKisa, iptalOzeti, kalanMetni, kombinasyonAdi, tutarYaz, type SatirDurumu, type Secim } from './mantik';
import type { OtelGirdisi, OtelSonucu } from './tipler';

export interface SatirVeri {
  readonly otel: OtelGirdisi;
  readonly sonuc: OtelSonucu | null;
  readonly durum: SatirDurumu;
  readonly secim: Secim;
  readonly isaretli: boolean;
  readonly enDusuk: boolean;
  readonly kapak: HotelPictureView | null;
  readonly seritAcik: boolean;
  readonly yenileniyor: boolean;
  readonly seritKimligi: string;
  /** Yazılan satış fiyatı (satisFiyatlari kaydı); yoksa boş. */
  readonly satisDeger?: string;
  /** Düzenleme uygulanmamış sistem toplamı ve para birimi; oda seçimi eksikse null. */
  readonly sistemMinor?: number | null;
  readonly sistemPara?: string | null;
  /** Şerit açıksa şerit düğümü (tabloda satırın altına yerleşir). */
  readonly serit: ReactNode;
}

const bas = (ad: string): string => ad.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p.charAt(0).toLocaleUpperCase('tr-TR')).join('');

function Kapak({ ad, kapak }: { ad: string; kapak: HotelPictureView | null }) {
  // Yanlış kare, kare yokluğundan kötüdür: kapak yalnız aynı slug'ın kataloğundan gelir, yoksa baş harfler.
  if (kapak !== null) return <img className="ht-kapak" src={kapak.src} srcSet={kapak.srcset || undefined} sizes="36px" alt="" loading="lazy" decoding="async" />;
  return <span className="ht-kapak ht-kapak-bos" aria-hidden="true">{bas(ad)}</span>;
}

function IptalHucresi({ s }: { s: SatirVeri }) {
  const h = s.durum.hesap;
  const ozet = iptalOzeti((h?.secilenler ?? []).map((x) => x.secenek));
  const sure = s.durum.sure;
  const kalan = s.durum.kalan;
  return (
    <div className="ht-iptal">
      {ozet.tur === 'ucretsiz' && ozet.sonTarih !== null ? <span className="ht-et ht-et-ok"><Ikon ad="kalkan" kucuk />{M.iptalUcretsiz(gunAyKisa(ozet.sonTarih))}</span> : null}
      {ozet.tur === 'iadesiz' ? <span className="ht-et ht-et-tehlike"><Ikon ad="kilit" kucuk />{M.iptalYok}</span> : null}
      {ozet.tur === 'bilinmiyor' ? <span className="ht-et"><Ikon ad="bilgi" kucuk />{M.iptalBilinmiyor}</span> : null}
      {sure === 'doldu' ? <span className="ht-et ht-et-tehlike"><Ikon ad="uyari" kucuk />{M.suresiDoldu}</span> : null}
      {(sure === 'bilgi' || sure === 'kritik') && kalan !== null ? <span className={`ht-et ht-et-${sure === 'kritik' ? 'tehlike' : 'uyari'}`}><Ikon ad="uyari" kucuk /><span data-testid="ht-kalan">{kalanMetni(kalan)}</span></span> : null}
      {sure === 'ok' && kalan !== null ? <span className="ht-sure"><Ikon ad="saat" kucuk /><span data-testid="ht-kalan">{kalanMetni(kalan)}</span></span> : null}
    </div>
  );
}

export function SonucTablosu({
  satirlar,
  onek,
  tekSecim,
  grupAdi,
  toplamBasligi,
  gece,
  sirali,
  salt,
  isaretle,
  seritDegistir,
  yenile,
  tabloEtkilesim,
  tusBasildi,
  satisDegistir,
}: {
  satirlar: readonly SatirVeri[];
  /** Test kimliği öneki: ilk bölümde `ht`, sonrakilerde `ht-b1`, `ht-b2`. */
  onek: string;
  /** Çoklu bölümde her bölümde tek seçim: radyo. */
  tekSecim: boolean;
  grupAdi: string;
  toplamBasligi: string;
  gece: number;
  sirali: boolean;
  salt: boolean;
  isaretle: (girdi: string, deger: boolean) => void;
  seritDegistir: (girdi: string) => void;
  yenile: (girdi: string) => void;
  tabloEtkilesim: () => void;
  tusBasildi: (girdi: string, e: React.KeyboardEvent) => void;
  /** Satış fiyatını satırda düzenler (boş metin = sistem fiyatı); verilmezse düzenleme görünmez. */
  satisDegistir?: (girdi: string, v: string) => void;
}) {
  return (
    <div className="ht-tablokap" role="region" aria-label={M.tabloAciklama} tabIndex={0} onPointerDown={tabloEtkilesim} onFocusCapture={tabloEtkilesim}>
      <table className="ht-tablo" data-testid={`${onek}-tablo`}>
        <caption className="ht-sr">{M.tabloAciklama}</caption>
        <thead>
          <tr>
            <th scope="col" className="ht-k-onay"><span className="ht-sr">{M.kolonOnay}</span></th>
            <th scope="col">{M.kolonOtel}</th>
            <th scope="col">{M.kolonOda}</th>
            <th scope="col">{M.kolonIptal}</th>
            <th scope="col" className="ht-sag" aria-sort={sirali ? 'ascending' : 'none'}>{toplamBasligi}</th>
            <th scope="col" className="ht-k-yenile"><span className="ht-sr">{M.kolonYenile}</span></th>
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s) => {
            const d = s.durum;
            const ad = s.sonuc?.otelAdi ?? s.otel.ad;
            const durumMetni = d.kod === 'uymuyor' ? M.odaUymuyor(d.hesap?.uymayanOdalar[0] ?? 1) : M.durum[d.kod];
            const hesap = d.hesap;
            const secenekSayisi = s.sonuc?.odaGruplari.reduce((t, g) => t + g.secenekler.length, 0) ?? 0;
            const fiyatli = hesap !== null && hesap.toplamMinor !== null;
            const eski = d.kod === 'eski';
            const bitisHata = s.sonuc !== null && s.sonuc.durum === 'HATA';
            const yenileDugme = (
              <td className="ht-k-yenile">
                {d.kod === 'yanit' && !s.yenileniyor ? (
                  <button type="button" className="ht-btn" onClick={() => yenile(s.otel.girdi)} disabled={salt}>
                    <Ikon ad="yenile" kucuk />{M.yenidenDene}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={d.kod === 'sure' ? 'ht-btn ht-btn-p' : 'ht-ikon-dugme'}
                    aria-label={M.yenile(ad)}
                    aria-disabled={salt || s.sonuc === null || s.yenileniyor}
                    onClick={() => { if (!salt && s.sonuc !== null && !s.yenileniyor) yenile(s.otel.girdi); }}
                    data-testid={`${onek}-yenile-${s.otel.girdi}`}
                  >
                    <Ikon ad="yenile" kucuk />{d.kod === 'sure' ? M.kolonYenile : null}
                  </button>
                )}
              </td>
            );
            return (
              <Fragment key={s.otel.girdi}>
                <tr
                  className={`ht-satir${s.isaretli && d.secilebilir ? ' ht-satir-secili' : ''}`}
                  data-testid={`${onek}-satir-${s.otel.girdi}`}
                  data-durum={d.kod}
                  onKeyDown={(e) => tusBasildi(s.otel.girdi, e)}
                >
                  <td className="ht-k-onay">
                    <input
                      type={tekSecim ? 'radio' : 'checkbox'}
                      name={tekSecim ? grupAdi : undefined}
                      className={tekSecim ? 'ht-cb ht-rb' : 'ht-cb'}
                      checked={s.isaretli}
                      disabled={(!d.secilebilir && !s.isaretli) || salt}
                      aria-label={d.secilebilir ? M.sec(ad, durumMetni) : M.secKapali(ad, durumMetni)}
                      onChange={(e) => isaretle(s.otel.girdi, e.target.checked)}
                      data-testid={`${onek}-onay-${s.otel.girdi}`}
                    />
                  </td>
                  <th scope="row" className="ht-otel">
                    <Kapak ad={ad} kapak={s.kapak} />
                    <span className="ht-otel-metin">
                      <span className="ht-otel-ad">{ad}</span>
                      <span className="ht-otel-meta">
                        {s.otel.yildiz !== null ? <Yildizlar n={s.otel.yildiz} /> : null}
                        {s.otel.otelNo !== null ? <span className="ht-otelno">{s.otel.otelNo}</span> : null}
                      </span>
                    </span>
                    {d.yeni ? <span className="ht-yeni">{M.yeniGeldi}</span> : null}
                  </th>
                  {s.sonuc === null || bitisHata ? (
                    <td colSpan={3} className="ht-durum-hucre">
                      <DurumRozeti kod={d.kod} metin={d.kod === 'yok' && s.sonuc !== null ? (enAzGece(s.sonuc) !== null ? M.yokMin(enAzGece(s.sonuc) ?? 0) : M.durum.yok) : undefined} />
                      {d.kod === 'cekiliyor' || s.yenileniyor ? <span className="ht-ilerleme" aria-hidden="true"><i /></span> : null}
                      {s.sonuc !== null ? <p className="ht-ipucu">{aciklama(s.sonuc, d.kod, gece)}</p> : null}
                    </td>
                  ) : (
                    <>
                      <td className="ht-kombinasyon">
                        <div className="ht-kombinasyon-ad">{hesap !== null && hesap.secilenler.length > 0 ? kombinasyonAdi(hesap.secilenler) : ''}{hesap !== null && hesap.uymayanOdalar.length > 0 ? ` (${M.odaUymuyor(hesap.uymayanOdalar[0] ?? 1)})` : ''}</div>
                        {hesap !== null && hesap.secilenler.length > 0 ? (
                          <div className="ht-etiketler">
                            {hesap.secilenler.every((x) => x.secenek.kahvalti === 'KAHVALTI_DAHIL') ? <span className="ht-et ht-et-ok"><Ikon ad="fincan" kucuk />{M.kahvaltiDahil}</span> : null}
                            {hesap.secilenler.some((x) => x.secenek.kahvalti === 'YALNIZ_ODA') ? <span className="ht-et"><Ikon ad="fincan" kucuk />{M.kahvaltisiz}</span> : null}
                            {hesap.secilenler.some((x) => x.secenek.kahvalti !== 'KAHVALTI_DAHIL' && x.secenek.kahvalti !== 'YALNIZ_ODA') ? <span className="ht-et"><Ikon ad="fincan" kucuk />{hesap.secilenler.map((x) => M.pansiyon[x.secenek.kahvalti]).filter((v, i, a) => a.indexOf(v) === i).join(', ')}</span> : null}
                            {d.kod === 'uymuyor' ? <DurumRozeti kod="uymuyor" odaNo={hesap.uymayanOdalar[0] ?? 1} /> : null}
                            {eski ? <DurumRozeti kod="eski" /> : null}
                          </div>
                        ) : (
                          <div className="ht-etiketler"><DurumRozeti kod={d.kod === 'eski' ? 'eski' : 'uymuyor'} odaNo={hesap?.uymayanOdalar[0] ?? 1} /></div>
                        )}
                        <button
                          type="button"
                          className="ht-baglanti ht-serit-dugme"
                          aria-expanded={s.seritAcik}
                          aria-controls={s.seritKimligi}
                          onClick={() => seritDegistir(s.otel.girdi)}
                          data-testid={`${onek}-serit-dugme-${s.otel.girdi}`}
                        >
                          <Ikon ad="yatak" kucuk />{M.odaTipleri(secenekSayisi)}
                        </button>
                      </td>
                      <td className="ht-iptal-hucre"><IptalHucresi s={s} /></td>
                      <td className="ht-sag ht-toplam-hucre">
                        {fiyatli && hesap !== null && hesap.toplamMinor !== null && hesap.paraBirimi !== null ? (
                          <>
                            <div className={`ht-toplam${d.kod === 'sure' || eski ? ' ht-eski-fiyat' : ''}`} data-testid={`${onek}-toplam-${s.otel.girdi}`}>{tutarYaz(hesap.toplamMinor)}</div>
                            <div className="ht-para">{hesap.paraBirimi}</div>
                            {d.kod === 'sure' ? <div className="ht-uyari-yazi">{M.guncelDegil}</div> : eski ? <div className="ht-uyari-yazi">{M.durum.eski}</div> : s.enDusuk ? <div className="ht-en-dusuk">{M.enDusuk}</div> : null}
                            {satisDegistir !== undefined && s.sistemMinor !== undefined && s.sistemMinor !== null && s.sistemPara !== undefined && s.sistemPara !== null ? <SatirFiyatDuzenle onek={onek} girdi={s.otel.girdi} otelAdi={s.sonuc?.otelAdi ?? s.otel.ad} deger={s.satisDeger ?? ''} sistemMinor={s.sistemMinor} para={s.sistemPara} toplamMinor={hesap.toplamMinor} salt={salt} degistir={satisDegistir} /> : null}
                          </>
                        ) : null}
                      </td>
                    </>
                  )}
                  {yenileDugme}
                </tr>
                {s.seritAcik && s.serit !== null ? (
                  <tr className="ht-serit-satir">
                    <td colSpan={6}>{s.serit}</td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function aciklama(s: OtelSonucu, kod: string, gece: number): string {
  const sinif = s.hata?.sinif;
  if (kod === 'talep') return sinif === 'FIYAT_ISTEGE_BAGLI' ? M.talepAcik : `${s.hata?.aciklama ?? ''} ${M.talepAcik}`.trim();
  if (kod === 'yok') return sinif === 'MUSAIT_DEGIL' || sinif === 'EN_AZ_GECE' ? M.yokAcik(gece, enAzGece(s)) : (s.hata?.aciklama ?? '');
  if (kod === 'yanit') return M.yanitAcik;
  if (kod === 'eski') return M.eskiAcik;
  return s.hata?.aciklama ?? '';
}
